// src/components/Header.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/apex-logo.png';
import {
    isAuthenticated,
    getActiveRole,
    getRoles,
    targetPathForRole,
    setActiveRole,
    logoutAndRedirect,
} from '../utils/auth';
import { useMobileNav } from '../context/MobileNavContext';

const Header = ({ showMenuButton = false }) => {
    const navigate = useNavigate();
    const authed = isAuthenticated();
    const activeRole = getActiveRole();
    const roles = getRoles();
    const [open, setOpen] = useState(false);
    const { isOpen: navOpen, toggle: toggleNav } = useMobileNav();

    const profilePath =
        activeRole === 'ADMIN' ? '/admin/profile' :
            activeRole === 'MANAGER' ? '/manager/profile' :
                activeRole === 'COACH' ? '/coach/profile' :
                    activeRole === 'STUDENT' ? '/student/profile' :
                        '/login';

    const canSwitch = roles.length > 1;

    const handleLogout = () => {
        setOpen(false);
        logoutAndRedirect();
    };

    const handleSwitch = (role) => {
        setActiveRole(role);
        setOpen(false);
        navigate(targetPathForRole(role), { replace: true });
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            'ADMIN': 'bg-danger-500',
            'MANAGER': 'bg-primary-600',
            'COACH': 'bg-warning-500',
            'STUDENT': 'bg-success-500',
        };
        return colors[role] || 'bg-gray-500';
    };

    return (
        <header className="bg-gradient-to-r from-primary-700 via-primary-600 to-primary-700 text-white shadow-md sticky top-0 z-50 pt-[env(safe-area-inset-top,0px)]">
            <div className="px-2 xs:px-3 sm:px-6 py-2 sm:py-3">
                <div className="flex items-center justify-between gap-1.5 sm:gap-2">
                    {/* Menu + Logo and Brand */}
                    <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
                        {showMenuButton && authed && (
                            <button
                                type="button"
                                onClick={toggleNav}
                                className="lg:hidden flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors"
                                aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
                                aria-expanded={navOpen}
                                aria-controls="app-sidebar"
                            >
                                {navOpen ? (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        )}
                        <div className="w-9 h-9 sm:w-14 sm:h-14 flex-shrink-0 bg-white rounded-xl p-1 shadow-lg hover:shadow-xl transition-shadow">
                            <img src={logo} alt="Apex Chess Academy" className="w-full h-full object-cover rounded-lg" />
                        </div>
                        <div className="min-w-0 hidden min-[360px]:block">
                            <h1 className="text-sm sm:text-xl font-bold tracking-tight truncate">
                                <span className="min-[420px]:hidden">Apex</span>
                                <span className="hidden min-[420px]:inline">Apex Chess Academy</span>
                            </h1>
                            <p className="text-xs text-primary-100 font-medium hidden sm:block">Believe in Moves!</p>
                        </div>
                    </div>

                    {/* Right side - User menu */}
                    {authed && (
                        <div className="flex items-center gap-3">
                            {/* Active role badge */}
                            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                                <span className={`w-2 h-2 rounded-full ${getRoleBadgeColor(activeRole)}`}></span>
                                <span className="text-sm font-medium uppercase tracking-wider">
                                    {activeRole || 'ROLE'}
                                </span>
                            </div>

                            {/* User menu dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setOpen((o) => !o)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 border border-white/20"
                                    aria-haspopup="menu"
                                    aria-expanded={open ? 'true' : 'false'}
                                >
                                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <svg className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {open && (
                                    <>
                                        {/* Backdrop */}
                                        <div
                                            className="fixed inset-0 z-40"
                                            onClick={() => setOpen(false)}
                                        ></div>

                                        {/* Dropdown menu */}
                                        <div
                                            role="menu"
                                            className="absolute right-0 mt-2 w-56 bg-white text-gray-800 rounded-xl shadow-hard z-50 overflow-hidden border border-gray-100 animate-fade-in"
                                        >
                                            {/* Profile button */}
                                            <button
                                                onClick={() => { setOpen(false); navigate(profilePath); }}
                                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                                                role="menuitem"
                                            >
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <span className="text-sm font-medium">My Profile</span>
                                            </button>

                                            {/* Switch Role section */}
                                            {canSwitch && (
                                                <>
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-t border-b border-gray-100">
                                                        Switch Role
                                                    </div>
                                                    {roles.map((r) => (
                                                        <button
                                                            key={r}
                                                            onClick={() => handleSwitch(r)}
                                                            className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${r === activeRole ? 'bg-primary-50' : ''}`}
                                                            role="menuitem"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <span className={`w-2 h-2 rounded-full ${getRoleBadgeColor(r)}`}></span>
                                                                <span className={`text-sm ${r === activeRole ? 'font-semibold text-primary-700' : ''}`}>
                                                                    {r}
                                                                </span>
                                                            </div>
                                                            {r === activeRole && (
                                                                <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    ))}
                                                </>
                                            )}

                                            {/* Logout button */}
                                            <div className="border-t border-gray-100">
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full flex items-center gap-3 px-4 py-3 text-danger-600 hover:bg-danger-50 transition-colors"
                                                    role="menuitem"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                    </svg>
                                                    <span className="text-sm font-medium">Logout</span>
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;

